import { z } from 'zod';

import { apiMediasPost, apiPurchaseProofsPost } from '@r/api/generated/client.ts';

export const MAX_PURCHASE_PROOF_SIZE = 5 * 1024 * 1024;
export const PURCHASE_PROOF_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'] as const;

type PurchaseProofValidationMessages = {
  required: string;
  invalidType: string;
  tooLarge: string;
};

/** Builds the purchase-proof form schema with translated validation messages. */
export function createPurchaseProofFormSchema(messages: PurchaseProofValidationMessages) {
  return z.object({
    file: z
      .custom<File>((value) => value instanceof File, messages.required)
      .refine(
        (file) =>
          PURCHASE_PROOF_MIME_TYPES.includes(
            file.type as (typeof PURCHASE_PROOF_MIME_TYPES)[number]
          ),
        messages.invalidType
      )
      .refine((file) => file.size <= MAX_PURCHASE_PROOF_SIZE, messages.tooLarge),
    comment: z.string().optional()
  });
}

export type PurchaseProofFormValues = z.infer<ReturnType<typeof createPurchaseProofFormSchema>>;

/** Uploads the selected file, then creates its associated purchase proof. */
export async function submitPurchaseProof({ file, comment }: PurchaseProofFormValues) {
  const media = await apiMediasPost({ file, type: 'proof' });

  return apiPurchaseProofsPost({
    filename: file.name,
    media: media.iri,
    comment: comment?.trim() || null
  });
}
